import type { OdometerReadingRow, RecurringTaskRow, WorkOrderRow } from '@routeback/core';

/**
 * Mileage between odometer readings (0045). RouteBack only hears a truck's
 * miles from the monthly MMR and from finished jobs with an odometer, so it
 * estimates in between: the truck's average miles per day over its readings
 * of the last ~6 months, carried forward from its latest reading. Each new
 * reading re-anchors it. Same rules as private.truck_mileage() — the server
 * keeps the stored due dates right; this shows them at once, offline too.
 */
export interface Mileage { anchorMiles: number; anchorOn: string; perDay: number | null }

const DAY = 86_400_000;
const toDay = (iso: string): number => Date.parse(`${iso.slice(0, 10)}T12:00:00Z`);
const fromDay = (t: number): string => new Date(t).toISOString().slice(0, 10);
export const daysBetween = (a: string, b: string): number => Math.round((toDay(b) - toDay(a)) / DAY);
export const addDays = (iso: string, n: number): string => fromDay(toDay(iso) + n * DAY);

export function truckMileage(readings: OdometerReadingRow[], truckId: string): Mileage | null {
  const byDay = new Map<string, number>();
  for (const r of readings) {
    if (r.deleted_at || r.truck_id !== truckId) continue;
    byDay.set(r.read_on, Math.max(byDay.get(r.read_on) ?? -Infinity, r.reading));
  }
  const days = [...byDay.keys()].sort();
  if (!days.length) return null;
  const lastOn = days[days.length - 1]!;
  const last = byDay.get(lastOn)!;
  const baseOn = days.find((d) => { const gap = daysBetween(d, lastOn); return gap >= 14 && gap <= 180; });
  let perDay: number | null = null;
  if (baseOn) {
    const rate = (last - byDay.get(baseOn)!) / daysBetween(baseOn, lastOn);
    if (rate > 0 && rate <= 1500) perDay = rate;
  }
  return { anchorMiles: last, anchorOn: lastOn, perDay };
}

/** Estimated odometer on a day (the latest reading when there's no rate yet). */
export function milesOn(m: Mileage | null, day: string): number | null {
  if (!m) return null;
  if (m.perDay == null) return m.anchorMiles;
  return Math.max(0, Math.round(m.anchorMiles + m.perDay * daysBetween(m.anchorOn, day)));
}

/** The day the truck should reach `miles` (null when it can't be told yet). */
export function milesDueDate(m: Mileage | null, miles: number | null | undefined): string | null {
  if (!m || miles == null) return null;
  if (m.perDay == null) return miles <= m.anchorMiles ? m.anchorOn : null;
  return addDays(m.anchorOn, Math.ceil((miles - m.anchorMiles) / m.perDay));
}

const earlier = (a: string | null | undefined, b: string | null | undefined): string | null => (a && b ? (a < b ? a : b) : a || b || null);

/** When a booked job is due: its date, or for a mileage job the earlier of the date limit and the estimated day. */
export function effectiveDue(w: Pick<WorkOrderRow, 'due_on' | 'due_miles' | 'due_calendar'>, m: Mileage | null): string | null {
  if (w.due_miles == null) return w.due_on;
  return earlier(w.due_calendar, milesDueDate(m, w.due_miles));
}

/** The due fields for a new booking: `firstDue` for the days, `milesFromNow` past today's estimate for the miles. */
export function booking(task: Pick<RecurringTaskRow, 'interval_days' | 'interval_miles'>, m: Mileage | null, today: string, firstDue: string | null, milesFromNow: number | null):
  Pick<WorkOrderRow, 'due_on' | 'due_miles' | 'due_calendar'> {
  const cal = task.interval_days ? firstDue : null;
  if (!task.interval_miles) return { due_on: cal };   // no mileage fields at all (see blankWorkOrder)
  const now = milesOn(m, today);
  const dm = now != null ? now + (milesFromNow ?? task.interval_miles) : null;
  // No reading yet: the truck's first MMR sets the mileage (server: private.odometer_moves_due).
  if (dm == null) return { due_on: cal, due_miles: null, due_calendar: cal };
  const w = { due_on: null, due_miles: dm, due_calendar: cal };
  return { ...w, due_on: effectiveDue(w, m) };
}

/** "every 10,000 mi or 6 months" (whichever comes first). */
export function ruleLabel(task: Pick<RecurringTaskRow, 'interval_days' | 'interval_miles'>, everyDays: (d: number | null) => string): string {
  const days = everyDays(task.interval_days);
  if (!task.interval_miles) return days;
  const mi = `every ${task.interval_miles.toLocaleString('en-US')} mi`;
  return days ? `${mi} or ${days.replace(/^every /, '')}` : mi;
}
